import { useQuery } from '@tanstack/react-query'
import { Col, Empty, Flex, Pagination, Row, Skeleton } from 'antd'
import { PropertyActivityPtoType, PtoItemType, SearchPTOParams } from 'Modules'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { useDebounce } from 'hooks/use-debounce'
import useSearchParams from 'hooks/use-search-params'
import { PtoCard } from 'modules/property-activities/pto-card'
import { PtoListFilter } from 'modules/property-activities/pto-list-filter'
import { ROUTES } from 'routes'

import './styles.scss'

interface PtoListParams extends SearchPTOParams {
  type?: PropertyActivityPtoType | 'ALL'
}

const fetchPtos = async (params: PtoListParams) => {
  const apiParams: SearchPTOParams = {
    page: params.page,
    limit: params.limit,
    keyword: params.keyword,
    ptoType: params.type && params.type !== 'ALL' ? params.type : undefined,
  }
  const response = await AppAdmin.getListPTO(apiParams)
  return {
    results: (response.data?.data?.results ?? []) as PtoItemType[],
    total: response.data?.data?.totalResults ?? 0,
  }
}

const PropertyActivitiesList = () => {
  const navigate = useNavigate()
  const { searchParams, setSearchParams } = useSearchParams<PtoListParams>({
    type: 'ALL',
  } as PtoListParams)

  const [searchText, setSearchText] = useState('')
  const debouncedSearchText = useDebounce(searchText, 300)

  useEffect(() => {
    setSearchParams((pre) => ({
      ...pre,
      keyword: debouncedSearchText || undefined,
      page: 1,
    }))
  }, [debouncedSearchText, setSearchParams])

  const { data, isLoading } = useQuery({
    queryKey: ['propertyActivities', 'ptos', searchParams],
    queryFn: () => fetchPtos(searchParams),
  })

  const ptos = useMemo(() => data?.results ?? [], [data])
  const total = data?.total ?? 0

  const handleTypeChange = (value: PropertyActivityPtoType | 'ALL') => {
    setSearchParams((pre) => ({ ...pre, type: value, page: 1 }))
  }

  const handlePtoClick = (pto: PtoItemType) => {
    navigate(`${ROUTES.PROPERTY_ACTIVITIES}/${pto._id}`)
  }

  return (
    <Flex vertical gap={24} className="property-activities">
      <Breadcrumb additionalItems={[BREADCRUMBS.PROPERTY_ACTIVITIES_NO_LINK]} />

      <Flex justify="space-between" align="flex-start">
        <div>
          <div className="main-title">Property Activities Management</div>
          <div className="main-description">Select a PTO to manage its activities and posts</div>
        </div>
      </Flex>

      <PtoListFilter
        searchText={searchText}
        onSearchChange={setSearchText}
        type={searchParams.type ?? 'ALL'}
        onTypeChange={handleTypeChange}
      />

      {isLoading ? (
        <Row gutter={[16, 16]}>
          {[1, 2, 3].map((k) => (
            <Col xs={24} md={12} xl={8} key={k}>
              <Skeleton active />
            </Col>
          ))}
        </Row>
      ) : ptos.length === 0 ? (
        <Empty description="No PTOs found" />
      ) : (
        <Row gutter={[16, 16]}>
          {ptos.map((pto) => (
            <Col xs={24} md={12} xl={8} key={pto._id}>
              <PtoCard pto={pto} onClick={handlePtoClick} />
            </Col>
          ))}
        </Row>
      )}

      {total > (searchParams.limit ?? 10) && (
        <Flex justify="flex-end">
          <Pagination
            current={searchParams.page}
            pageSize={searchParams.limit}
            total={total}
            onChange={(page, pageSize) => {
              setSearchParams((pre) => ({ ...pre, page, limit: pageSize }))
            }}
            showSizeChanger
          />
        </Flex>
      )}
    </Flex>
  )
}

export default PropertyActivitiesList
